// Confirm before publishing a roster: everyone on it is emailed and pushed and
// asked to accept or decline. Built on the kit Modal so it gets the dialog
// role, focus trap, Esc and the scrim like every other confirmation.
import { Send } from 'lucide-react';
import type { TeamMemberAssignment } from '@app/types';
import { Button, Modal, StatusPill } from '@app/components/ui';

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: () => void;
  team?: TeamMemberAssignment[];
  isPublishing?: boolean;
}

export const PublishModal: React.FC<PublishModalProps> = ({ isOpen, onClose, onPublish, team, isPublishing }) => (
  // kind="confirm" names the dialog from `title` but only for screen readers,
  // so the visible heading is drawn here (as ConfirmDialog does).
  <Modal open={isOpen} onClose={onClose} kind="confirm" width={440} dismissible={!isPublishing} title="Publish roster?">
    <div className="w-11 h-11 rounded-[13px] bg-acc-bg2 text-acc flex items-center justify-center mb-4" aria-hidden="true">
      <Send size={19} strokeWidth={2} />
    </div>
    <div className="text-[20px] font-semibold tracking-[-0.01em]" aria-hidden="true">
      Publish roster?
    </div>
    <p className="text-[14px] text-t3 mt-2 leading-[1.55]">Everyone on it gets an email and a push notification asking them to accept or decline.</p>
    {team && team.length > 0 && (
      <div className="rounded-[14px] bg-fill p-3 mt-4">
        <div className="wp-eyebrow mb-2">Team ({team.length})</div>
        <ul className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
          {team.map((m, i) => (
            <li key={m.id || `${m.userId}-${i}`} className="flex items-center gap-2 text-[13px]">
              <span className="font-medium truncate">{m.name}</span>
              <span className="text-t4 truncate flex-1">{m.role}</span>
              {m.isPlaceholder && <StatusPill tone="muted">Guest</StatusPill>}
            </li>
          ))}
        </ul>
      </div>
    )}
    <div className="flex gap-2.5 mt-[22px]">
      <Button variant="ghost" shape="form" size="lg" className="flex-1" onClick={onClose} disabled={isPublishing}>
        Cancel
      </Button>
      <Button shape="form" size="lg" className="flex-1" loading={isPublishing} onClick={onPublish}>
        Publish now
      </Button>
    </div>
  </Modal>
);
